/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { IPersonaProps } from '@fluentui/react/lib/Persona';
import { ValidationState } from '@fluentui/react/lib/Pickers';

import { searchChannelConversationRoster } from '../channelExperience/ChannelPeopleResults';
import { ChannelRosterState } from '../channelExperience/ChannelRosterState';
import { LogCategory } from '../logger/LogCategories';
import { ApprovalPersonaProps } from '../models/ApprovalPersonaProps';
import { PeopleResultsClient } from '../service/gen';
import { PeoplePickerClient, PeopleResult, VisibilityPrincipalType } from '../service/gen/templates';
import { newGuid } from './AppUtils';
import { getAppHost } from './TeamsUtils';
import * as Utils from './Utils';

const peopleResultsClient = new PeopleResultsClient();
const templatePeoplePickerClient = new PeoplePickerClient();

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Get the users for the chat context.
 *
 * @param chatId Chat ID for the Teams context.
 * @return People Results.
 */
export const getChatConversationRoster = async (chatId: string): Promise<PeopleResult[]> => {
    const peopleResults = getAppHost().isNewChat()
        ? []
        : await peopleResultsClient.getChatPeopleResultsGET(chatId, newGuid());

    return peopleResults;
};

/**
 * Get the filtered list of users for the team context.
 *
 * @param queryString Input string used to filter users
 * @param teamId Team ID for the group context.
 * @return People Results.
 */
export const getFilteredTeamRoster = async (queryString: string, teamId: string): Promise<PeopleResult[]> => {
    const normalizedInput = normalizeQueryInput(queryString);

    if (normalizedInput) {
        const peopleResults = await peopleResultsClient.getTeamPeopleResults(teamId, {
            QueryText: normalizedInput,
        });

        return peopleResults;
    }

    return Promise.resolve([]);
};

/**
 * Get the filtered list of users and groups for the tenant context.
 *
 * @param queryString Input string used to filter users and groups
 * @return People Results.
 */
export const getFilteredUsersAndGroupsRoster = async (queryString: string): Promise<PeopleResult[]> => {
    const normalizedInput = normalizeQueryInput(queryString);

    if (normalizedInput) {
        const peopleResults = await templatePeoplePickerClient.getTenantUsersAndGroups({
            QueryText: normalizedInput,
        });

        return peopleResults;
    }

    return Promise.resolve([]);
};

/**
 * Get the users in the same tenant in global context (personal app) based on a input string
 *
 * @param input Input string used to filter users
 * @return List of users who matches the input in the same tenant
 */
export const getTenantUsers = async (input: string): Promise<PeopleResult[]> => {
    const normalizedInput = normalizeQueryInput(input);

    if (normalizedInput) {
        const peopleResults = await peopleResultsClient.getTenantPeopleResults(newGuid(), {
            QueryText: normalizedInput,
        });

        return peopleResults;
    }

    return Promise.resolve([]);
};

export const normalizeQueryInput = (input: string): string => input?.trim();

/**
 * Validate raw text entered into People Picker
 *
 * @param inputText: input text entered by the user
 */
export const validateRawText = (_inputText: string): ValidationState => {
    // We force the user to select something from the people picker instead of entering raw text
    return ValidationState.invalid;
};

export const validateEmail = (inputText: string): ValidationState => {
    if (!inputText || !inputText.trim()) {
        return ValidationState.invalid;
    }

    if (emailRegex.test(inputText)) {
        return ValidationState.valid;
    }

    return ValidationState.warning;
};

/**
 * Sorts persona props list alphabetically by full name
 *
 * @parameter elements - A list of Persona objects
 * @parameter toLower - optional boolean indicating case should be ignored when comparing
 */
export function sortByText<T>(elements: T[], toLower: boolean = true): T[] {
    elements = elements.sort((t1: IPersonaProps, t2: IPersonaProps): number => {
        let fullName1 = t1.text != null ? t1.text : '';
        let fullName2 = t2.text != null ? t2.text : '';

        if (toLower) {
            fullName1 = fullName1.toLowerCase();
            fullName2 = fullName2.toLowerCase();
        }

        if (fullName1 > fullName2) {
            return 1;
        } else if (fullName1 < fullName2) {
            return -1;
        }

        return 0;
    });

    return elements;
}

const mergeUniquePeopleResults = (current: PeopleResult[], additional?: PeopleResult[]): PeopleResult[] => {
    const uniqueResults = Utils.removeDuplicates(
        additional || [],
        current,
        (a: PeopleResult, b: PeopleResult): boolean => a.UserAadObjectId === b.UserAadObjectId
    );
    return current.concat(uniqueResults);
};

/**
 * Determines if enough suggestions were provided by initial search.
 *
 * @param queryResults results of search query
 */
const needsRetry = (queryResults: PeopleResult[]): boolean => {
    const ecsConfig = getAppHost().getEcsConfig().MicrosoftTeamApprovals;
    return queryResults.length < ecsConfig.PeoplePickerRetrySearchThreshold;
};

type AdditionalPeopleSearch =
    | {
          type: 'channel';
          rosterState: ChannelRosterState;
      }
    | {
          type: 'team';
          teamId: string;
      }
    | {
          type: 'none';
      };

const getAdditionalPeopleSearch = (
    filterText: string,
    queryResults: PeopleResult[],
    teamId?: string,
    rosterState?: ChannelRosterState
): AdditionalPeopleSearch => {
    const rosterSnapshot = rosterState?.getSnapshot();
    if (
        rosterState &&
        rosterSnapshot?.compatibility !== 'legacyFallback' &&
        rosterSnapshot?.compatibility !== 'unavailable' &&
        filterText &&
        (rosterSnapshot?.rosterComplete !== true || rosterSnapshot?.audienceCouldNotBeEstablished)
    ) {
        return {
            type: 'channel',
            rosterState,
        };
    }

    if (teamId && (!rosterState || rosterSnapshot?.compatibility === 'legacyFallback') && needsRetry(queryResults)) {
        return {
            type: 'team',
            teamId,
        };
    }

    return {
        type: 'none',
    };
};

const searchChannelRosterSafely = async (
    filterText: string,
    rosterState: ChannelRosterState
): Promise<PeopleResult[]> => {
    const searchSequence = rosterState.beginPeopleSearch();
    try {
        const results = await searchChannelConversationRoster(filterText, rosterState);
        rosterState.recordPeopleSearchAvailability('available', searchSequence);
        return results;
    } catch (error) {
        rosterState.recordPeopleSearchAvailability('unavailable', searchSequence);
        getAppHost()
            .getLogger()
            .logTraceError(LogCategory.PeoplePicker, `Failed to search the channel roster: ${error}`);
        return [];
    }
};

/**
 * Get Personas from Local 'conversation' roster cache
 *
 * @param currentPersonas - current personas selected in a list of personas
 * @param people - list of personas
 * @param filterText: text used to filter results,
 * @param teamId - team the caller wants searched as a legacy fallback
 * @param onChannelSearched - reports whether the channel search endpoint was used
 * @param rosterState - channel-scoped roster state used to select and execute the search path
 */
export const getPeopleResultsForQuery = async (
    people: PeopleResult[],
    filterText: string,
    currentPersonas: IPersonaProps[],
    teamId?: string,
    onChannelSearched?: (searched: boolean) => void,
    rosterState?: ChannelRosterState
): Promise<IPersonaProps[]> => {
    let queryResults = rosterState?.hasRosterLoaded() ? rosterState.getRosterPeople() : people || [];

    // Lowercase the user input and strip leading and following spaces
    filterText = filterText.toLowerCase().trim();

    // Fetch results from server too in global context.
    if (getAppHost().isGlobalContext()) {
        queryResults = mergeUniquePeopleResults(queryResults, await getTenantUsers(filterText));
    }

    queryResults = filterResultsByNameAndEmail(queryResults, filterText);

    const additionalSearch = getAdditionalPeopleSearch(filterText, queryResults, teamId, rosterState);
    switch (additionalSearch.type) {
        case 'channel':
            onChannelSearched?.(true);
            queryResults = mergeUniquePeopleResults(
                queryResults,
                await searchChannelRosterSafely(filterText, additionalSearch.rosterState)
            );
            break;
        case 'team':
            queryResults = mergeUniquePeopleResults(
                queryResults,
                await getFilteredTeamRoster(filterText, additionalSearch.teamId)
            );
            break;
        case 'none':
            onChannelSearched?.(false);
            break;
    }

    let suggestedPersonas = mapPeopleResultsToPersonas(queryResults);
    suggestedPersonas = filterOutAlreadySelectedPeople(suggestedPersonas, currentPersonas);
    return sortByText(suggestedPersonas);
};

/**
 * Filter results list so it only contains users whose email or name begin with the filter text.
 *
 * @param results - list of people results to be filtered
 * @param filterText: text used to filter results
 */
const filterResultsByNameAndEmail = (results: PeopleResult[], filterText: string): PeopleResult[] => {
    // We filter out users based on the display name, so take out the users who don't have one.
    // It's surprising though that there are users without a display name.
    results = results.filter((result: PeopleResult): boolean => result.UserDisplayName !== null);

    const filterFunction = (t1: PeopleResult, text: string): boolean => {
        const fullName = t1.UserDisplayName != null ? t1.UserDisplayName : '';
        const emailAlias = t1.Email ? t1.Email : '';

        return emailAlias.toLowerCase().startsWith(text) || nameStartsWith(fullName.toLowerCase(), text);
    };

    results = Utils.filterByText(results, filterText, filterFunction);

    return results;
};

/**
 * Determines if any part of name (first, middle, last) starts with substring.
 *
 * @param fullName - full name of user
 * @param filterText - text used to filter results
 */
const nameStartsWith = (fullName: string, filterText: string): boolean => {
    return fullName.startsWith(filterText) || fullName.includes(` ${filterText}`);
};

/**
 * Get Personas from server side. No filtering or sorting.
 *
 * @param currentPersonas - current personas selected in a list of personas
 * @param people - list of personas
 * @param filterText: text used to filter results,
 */
export const getPeopleResultsForQueryInPersonalApp = async (
    people: PeopleResult[],
    filterText: string,
    currentPersonas: IPersonaProps[]
): Promise<IPersonaProps[]> => {
    let peopleList = people || [];

    // generaly, filter text should not be null or undefined, but if it is, let's make it the empty string
    if (filterText == null || filterText === undefined) {
        throw new Error('Unexpected value.');
    }

    // Lowercase the user input and strip leading and following spaces
    filterText = filterText.toLowerCase().trim();

    // Fetch results from server in global context
    try {
        let searchList = await getTenantUsers(filterText);
        searchList = Utils.removeDuplicates(
            searchList,
            people,
            (a: PeopleResult, b: PeopleResult): boolean => a.UserAadObjectId === b.UserAadObjectId
        );
        peopleList = peopleList.concat(searchList);
    } catch (ex) {
        // do nothing, this lets us test by catching a TokenNotInitialized error
    }

    let suggestedPersonas = mapPeopleResultsToPersonas(peopleList);
    suggestedPersonas = filterOutAlreadySelectedPeople(suggestedPersonas, currentPersonas);
    return sortByText(suggestedPersonas);
};

/**
 * Get Personas in specific team.
 *
 * @param filterText: text used to filter results,
 * @param teamId - team id
 * @param currentPersonas - current personas selected in a list of personas
 */
export const getPeopleResultsForQueryInTeam = async (
    filterText: string,
    teamId: string,
    currentPersonas: IPersonaProps[]
): Promise<IPersonaProps[]> => {
    let peopleList: PeopleResult[] = [];

    if (filterText == null || filterText === undefined) {
        throw new Error('Unexpected value.');
    }

    // Lowercase the user input and strip leading and following spaces
    filterText = filterText.toLowerCase().trim();

    // Fetch results from server in global context
    try {
        peopleList = await getFilteredTeamRoster(filterText, teamId);
    } catch (ex) {
        // do nothing, this lets us test by catching a TokenNotInitialized error
    }

    let suggestedPersonas = mapPeopleResultsToPersonas(peopleList);
    suggestedPersonas = filterOutAlreadySelectedPeople(suggestedPersonas, currentPersonas);
    return sortByText(suggestedPersonas);
};

/**
 * Get user and group personas in tenant wide.
 *
 * @param filterText: text used to filter results,
 * @param currentPersonas - current personas selected in a list of personas
 */
export const getUsersAndGroupsForQuery = async (
    filterText: string,
    currentPersonas: IPersonaProps[]
): Promise<IPersonaProps[]> => {
    let peopleList: PeopleResult[] = [];

    if (filterText == null || filterText === undefined) {
        throw new Error('Unexpected value.');
    }

    // Lowercase the user input and strip leading and following spaces
    filterText = filterText.toLowerCase().trim();

    // Fetch results from server in global context
    try {
        peopleList = await getFilteredUsersAndGroupsRoster(filterText);
    } catch (ex) {
        // do nothing, this lets us test by catching a TokenNotInitialized error
    }

    let suggestedPersonas = mapPeopleResultsToPersonas(peopleList);
    suggestedPersonas = filterOutAlreadySelectedPeople(suggestedPersonas, currentPersonas);
    return sortByText(suggestedPersonas);
};

/**
 * Remove people which have already been selected from the suggestions.
 *
 * @param peopleList - list of people suggestions
 * @param currentPersonas - list of current personas selected in a list of personas
 */
export const filterOutAlreadySelectedPeople = (
    peopleList: IPersonaProps[],
    currentPersonas: IPersonaProps[]
): IPersonaProps[] => {
    // TODO: log people result here, with results that people are receiving.
    getAppHost().getLogger().logNonUiTelemetry('PeoplePicker', 'success', {
        ListSize: peopleList.length,
    });

    return Utils.removeDuplicates(
        peopleList,
        currentPersonas,
        (a: IPersonaProps, b: IPersonaProps): boolean => a.id === b.id
    );
};

/**
 * Convert list of PeopleResults to list of IPersonaProps
 *
 * @param peopleList - list of people results
 */
export const mapPeopleResultsToPersonas = (peopleList: PeopleResult[]): IPersonaProps[] => {
    return peopleList.map((user: PeopleResult): IPersonaProps => {
        const result: ApprovalPersonaProps = {
            text: user.UserDisplayName,
            secondaryText: user.Email || user.UserUPN,
            id: user.UserAadObjectId,
            coinSize: getAppHost().isMobile() ? 36 : 30,
            email: user.Email,
            upn: user.UserUPN,
            // TODO: Use backend enum for group and user rather than hard code string
            personaType:
                user.PersonType?.Class === 'Group' ? VisibilityPrincipalType.Group : VisibilityPrincipalType.User,
        };
        return result;
    });
};
